import {
  Stack,
  Box,
  Text,
  Badge,
  Button,
  Flex,
  Tag,
  TagLabel,
  TagCloseButton,
  Alert,
  AlertIcon,
  useDisclosure,
  useToast,
  Divider,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { MdUploadFile, MdEdit, MdDelete } from 'react-icons/md';
import { ITitleItem, ITitleSubmissionItem, ITitleDocument } from '../../../../services/hooks/TitleSubmissions/types';
import { useDeleteTitleItem } from '../../../../services/hooks/TitleSubmissions/useTitleSubmissionItems';
import { useUploadTitleDocument } from '../../../../services/hooks/TitleSubmissions/useUploadTitleDocument';
import { useDeleteTitleDocument } from '../../../../services/hooks/TitleSubmissions/useDeleteTitleDocument';
import { useAuditLogger } from '../../../../services/hooks/Audit/useAuditLogger';
import TitleItemDeclarationModal from './TitleItemDeclarationModal';

interface IOwnProps {
  item: ITitleItem;
  declaration: ITitleSubmissionItem | undefined;
  submissionId: string;
  isDraft: boolean;
}

const SCORING_TYPE_LABEL: Record<string, string> = {
  fixed: 'Pontuação Fixa',
  per_unit: 'Por Unidade',
  per_qualis: 'Por Qualis',
  classified: 'Classificado',
  normalized: 'Normalizado',
  per_attributes: 'Por Atributos',
};

const TitleItemCard: React.FC<IOwnProps> = ({ item, declaration, submissionId, isDraft }) => {
  const toast = useToast();
  const auditLogger = useAuditLogger();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const deleteItem = useDeleteTitleItem(submissionId);
  const uploadDocument = useUploadTitleDocument(submissionId, declaration?.id ?? '');
  const deleteDocument = useDeleteTitleDocument(submissionId);

  const handleDeleteItem = async () => {
    if (!declaration) return;
    try {
      await deleteItem.mutateAsync(declaration.id);
      toast({ title: 'Item removido', status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast({
        title: 'Erro ao remover item',
        description: error?.response?.data?.detail || 'Ocorreu um erro.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !declaration) return;
    try {
      await uploadDocument.mutateAsync(file);
      await auditLogger.logDocumentAction(
        'document_uploaded',
        declaration.id,
        file.name,
        'info'
      );
      toast({ title: 'Comprovante enviado', status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast({
        title: 'Erro ao enviar comprovante',
        description: error?.response?.data?.detail || 'Ocorreu um erro.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!declaration) return;
    try {
      await deleteDocument.mutateAsync({ itemId: declaration.id, docId });
      await auditLogger.logDocumentAction('document_deleted', docId, docName, 'warning');
      toast({ title: 'Comprovante removido', status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast({
        title: 'Erro ao remover comprovante',
        description: error?.response?.data?.detail || 'Ocorreu um erro.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isDeclared = !!declaration;

  return (
    <>
      <Box
        borderWidth="1px"
        borderRadius="md"
        p={3}
        bg={isDeclared ? 'blue.50' : 'white'}
        borderColor={isDeclared ? 'blue.200' : 'gray.200'}
      >
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
          <Stack spacing={1} flex={1}>
            <Flex align="center" gap={2} wrap="wrap">
              <Text fontWeight="semibold" fontSize="sm">
                {item.name}
              </Text>
              <Badge colorScheme="purple" fontSize="xs">
                {SCORING_TYPE_LABEL[item.scoring_type] ?? item.scoring_type}
              </Badge>
              {isDeclared && (
                <Badge colorScheme="green" fontSize="xs">
                  Declarado
                </Badge>
              )}
              {isDeclared && declaration && declaration.documents.length > 0 && (
                <Badge colorScheme="blue" fontSize="xs">
                  Anexado
                </Badge>
              )}
            </Flex>

            {!isDeclared && (
              <Text fontSize="xs" color="gray.500">
                {item.points_per_unit != null
                  ? `${item.points_per_unit} pts por unidade`
                  : item.max_score != null
                  ? `Até ${item.max_score} pts`
                  : 'Consulte a tabela de pontuação'}
              </Text>
            )}

            {isDeclared && declaration && (
              <Text fontSize="xs" color="gray.600">
                {item.scoring_type !== 'fixed'
                  ? `${declaration.declared_quantity} unidade(s) — `
                  : ''}
                <strong>{Number(declaration.declared_score).toFixed(2)} pts</strong>
                {declaration.qualis_level ? ` · Qualis ${declaration.qualis_level}` : ''}
              </Text>
            )}

            {item.required_documents_json.length > 0 && (
              <Alert status="warning" py={1} px={2} borderRadius="md" mt={1}>
                <AlertIcon boxSize={3} />
                <Text fontSize="xs">
                  Documentos obrigatórios: {item.required_documents_json.join(', ')}
                </Text>
              </Alert>
            )}
          </Stack>

          {!isDeclared && isDraft && (
            <Button size="xs" colorScheme="blue" leftIcon={<MdEdit />} onClick={onOpen}>
              Declarar
            </Button>
          )}
        </Flex>

        {/* Documentos anexados */}
        {isDeclared && declaration && (
          <>
            <Divider my={2} />
            <Stack spacing={1}>
              {declaration.documents.length > 0 ? (
                <Flex wrap="wrap" gap={1}>
                  {declaration.documents.map((doc: ITitleDocument) => (
                    <Tag key={doc.id} size="sm" colorScheme="blue" borderRadius="full" cursor="pointer">
                      <TagLabel
                        fontSize="xs"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => window.open(doc.file, '_blank', 'noopener,noreferrer')}
                      >
                        {doc.name}
                      </TagLabel>
                      {isDraft && (
                        <TagCloseButton
                          onClick={() => handleDeleteDocument(doc.id, doc.name)}
                          isDisabled={deleteDocument.isPending}
                        />
                      )}
                    </Tag>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="xs" color="orange.500">
                  Nenhum comprovante anexado
                </Text>
              )}

              {isDraft && (
                <Flex gap={2} mt={1} wrap="wrap">
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <Button
                    size="xs"
                    leftIcon={<MdUploadFile />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    isLoading={uploadDocument.isPending}
                  >
                    Adicionar comprovante
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<MdEdit />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={onOpen}
                  >
                    Editar
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<MdDelete />}
                    colorScheme="red"
                    variant="outline"
                    onClick={handleDeleteItem}
                    isLoading={deleteItem.isPending}
                  >
                    Remover
                  </Button>
                </Flex>
              )}
            </Stack>
          </>
        )}
      </Box>

      <TitleItemDeclarationModal
        isOpen={isOpen}
        onClose={onClose}
        item={item}
        existing={declaration}
        submissionId={submissionId}
      />
    </>
  );
};

export default TitleItemCard;
